import { Injectable } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AppSettingService, AppUtilityService } from '../app.service';
import { UserInfoService } from '../user/user-info.service';
import { CacheService } from '../utility/cache.service';

export interface ICSCSearchOption {
	Degree: string,
	EnforceList: boolean,
	Mile: string,
	Page: number,
	PerPage: number,
	Program: string, // fields of study, degree, 4 digits
	Region: string,
	ReligiousAffiliation: string,
	SchoolName: string,
	SchoolType: string,
	SortBy: string,
	SortDir: string,
	SpecializedMission: string,
	State: string,
	StudentSize: string,
	Urbanicity: string,
	ZipCode: string,
	SchoolIdList: string[]
}

export class CSCSearchOption implements ICSCSearchOption {
	constructor() { }
	Degree = "";
	EnforceList = false;
	Mile = "100";
	Page =  0;
	PerPage = 6;
	Program = "";
	Region = "";
	ReligiousAffiliation = "";
	SchoolName = "";
	SchoolType = "";
	SortBy = "";
	SortDir = "";
	SpecializedMission = "";
	State = "";
	StudentSize = "";
	Urbanicity = "";
	ZipCode = "";
	SchoolIdList = [] as any[];
}

export interface ICSCSearchFilter {
	Degree: any,
	Mile: string,
	Program: string,
	Region: string,
	ReligiousAffiliation: string,
	SchoolName: string,
	SchoolType: any,
	SpecializedMission: string,
	State: string,
	StudentSize: any,
	ZipCode: string,
	Urbanicity: any,
	selectedSchool: any,
	selectedProgram: any
}

export class CSCSearchFilter implements ICSCSearchFilter {
	constructor() { }
	Degree = {};
	Mile = "";
	Program = "";
	Region = "";
	ReligiousAffiliation = "";
	SchoolName = "";
	SchoolType = {};
	SpecializedMission = "";
	State = "";
	StudentSize = {};
	ZipCode = "";
	Urbanicity = {};
	selectedSchool = null as any;
	selectedProgram = null as any;
}

export interface ICSCSearchName {
	Page: number,
	PerPage: number,
	SchoolName: string
}

@Injectable({
	providedIn: 'root'
})
export class CollegeScorecardAPIService {
	constructor(
		private cacheSrv: CacheService,
		private userInfo: UserInfoService,
		private http: HttpClient
	) { }

	private collegeScoreCardUrl = AppSettingService.appConfig.JourneyAPIUrl + "CollegeScorecard";

	getDegree(): Observable<any> {
		let l: string = this.collegeScoreCardUrl + "/Search/Option/degree";
		return this.cacheSrv.get(true, l);
	}

	searchDegree(kw: string, cip: string, limit: number, offset:number): Observable<any> {
		return this.http.get<any>(
			this.collegeScoreCardUrl + "/SearchDegree?keyword=" + encodeURIComponent(kw) + "&cip=" + encodeURIComponent(cip) + "&limit=" + limit.toString() + "&offset=" + offset.toString()
		);
	}

	getState(): Observable<any> {
		let l: string = this.collegeScoreCardUrl + "/Search/Option/state";
		return this.cacheSrv.get(true, l);
	}

	getRegion(): Observable<any> {
		let l: string = this.collegeScoreCardUrl + "/Search/Option/region";
		return this.cacheSrv.get(true, l);
	}

	getStudentSize(): Observable<any> {
		let l: string = this.collegeScoreCardUrl + "/Search/Option/studentsize";
		return this.cacheSrv.get(true, l);
	}

	getSchoolType(): Observable<any> {
		let l: string = this.collegeScoreCardUrl + "/Search/Option/type";
		return this.cacheSrv.get(true, l);
	}

	getUrbanicity(): Observable<any> {
		let l: string = this.collegeScoreCardUrl + "/Search/Option/urbanicity";
		return this.cacheSrv.get(true, l);
	}

	getSpecializedMission(): Observable<any> {
		let l: string = this.collegeScoreCardUrl + "/Search/Option/specializedmission";
		return this.cacheSrv.get(true, l);
	}

	getReligiousAffiliation(): Observable<any> {
		let l: string = this.collegeScoreCardUrl + "/Search/Option/religiousaffiliation";
		return this.cacheSrv.get(true, l);
	}

	searchSchool(searchOptions: ICSCSearchOption): Observable <any> {
		return this.http.post<any>(
			this.collegeScoreCardUrl + "/Search",
			searchOptions
		);
	}

	searchSchoolName(searchOptions: ICSCSearchName): Observable <any> {
		return this.http.post<any>(
			this.collegeScoreCardUrl + "/SearchSchoolName",
			searchOptions
		);
	}

	getSchool(schoolId: string): Observable<any> {
		let l: string = this.collegeScoreCardUrl + "/School/" + encodeURIComponent(schoolId);
		return this.cacheSrv.get(true, l);
	}

	getSchools(schoolIdList: string, page: number, perPage: number): Observable <any> {
		return this.http.get<any>(
			this.collegeScoreCardUrl + "/Search?page=" + page.valueOf() + "&perpage=" + perPage.toString() + "&schoolIdList=" + encodeURIComponent(schoolIdList)
		);
	}

	getSchoolSummary(schoolIdList:string): Observable <any> { 
		return this.http.get<any>(
			this.collegeScoreCardUrl + "/Summarize?schoolIdList=" + encodeURIComponent(schoolIdList)
		);
	}

	getFieldsOFStudy(schoolId: string): Observable<any> {
		return this.http.get<any>(
			this.collegeScoreCardUrl + "/School/" + encodeURIComponent(schoolId) + "/FieldsofStudy",
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}
}



@Injectable({
	providedIn: 'root'
})
export class CollegeScorecardService {
	constructor(
		private router: Router,
		private route: ActivatedRoute
	) { }

	degreeOptions = [] as any[];
	stateOptions = [] as any[];
	regionOptions = [] as any[];
	studentSizeOptions = [] as any[];
	schoolTypeOptions = [] as any[];
	urbanicityOptions = [] as any[];
	specializedMissionOptions = [] as any[];
	religiousAffiliationOptions = [] as any[];

	searchFilter: ICSCSearchFilter = new CSCSearchFilter();

	searchOption: ICSCSearchOption = new CSCSearchOption();

	initSearchFilter(): void {
		this.searchFilter = new CSCSearchFilter();
	};

	searchResult = {
		Status: 0,
		Page: 0,
		CurrentPage: 1,
		PerPage: 6,
		Total: null as any,
		Schools: [] as any[],
		Message: ""
	};

	initSearchResult() : void {
		this.searchResult.Status = 0;
		this.searchResult.Page = 0;
		this.searchResult.CurrentPage = 1;
		this.searchResult.Total = null;
		this.searchResult.Schools = [];
		this.searchResult.Message = "";
	};

	urlFilter = {
		hasFilter: false,
		FromName: "",
		FromURL: "",
		Mile: "",
		Program: "",
		ZipCode: "",
		urlString: "",
		SchoolName: ""
	};

	gatherUrlFilter(): void {
		let fromName: string = this.route.snapshot.queryParamMap.get("FromName");
		let fromURL: string = this.route.snapshot.queryParamMap.get("FromURL");
		let zipCode: string = this.route.snapshot.queryParamMap.get("ZipCode");
		let mile: string = this.route.snapshot.queryParamMap.get("Mile");
		let program: string = this.route.snapshot.queryParamMap.get("Program");
		let schoolName: string = this.route.snapshot.queryParamMap.get("SchoolName");

		if (fromName != null && fromURL != null) {
			this.urlFilter.hasFilter = true;
			this.urlFilter.FromName = (fromName??"").trim();
			this.urlFilter.FromURL = (fromURL??"").trim();
			this.urlFilter.ZipCode = (zipCode??"").trim();
			this.urlFilter.Mile = (mile??"").trim();
			this.urlFilter.Program = (program??"").trim();
			this.urlFilter.SchoolName = (schoolName??"").trim();

			// if search filter is different, initialize filter
			if (this.urlFilter.urlString != JSON.stringify(this.route.snapshot.queryParamMap)) {
				this.initSearchFilter();
				this.initSearchResult();
				this.urlFilter.urlString = JSON.stringify(this.route.snapshot.queryParamMap);
				this.searchFilter.Program = this.urlFilter.Program;
				this.searchFilter.ZipCode = this.urlFilter.ZipCode;
				this.searchFilter.Mile = this.urlFilter.Mile;
				this.searchFilter.SchoolName = this.urlFilter.SchoolName;
			}
		}
		else {
			this.urlFilter.hasFilter = false,
			this.urlFilter.FromName = "";
			this.urlFilter.FromURL = "";
			this.urlFilter.Mile = "";
			this.urlFilter.Program = "";
			this.urlFilter.ZipCode = "";
			this.urlFilter.SchoolName = "";
			this.urlFilter.urlString = "";
		}
	};

	gotoFrom() : void {
		if (this.urlFilter.hasFilter) {
			if (this.urlFilter.FromURL.indexOf(AppSettingService.baseUrl) === 0) {
				this.router.navigateByUrl(this.urlFilter.FromURL.replace(AppSettingService.baseUrl + "/", "/"));
			}
		}
	};

	gotoSearch() : void {
		this.router.navigateByUrl("/CollegeScorecard/Search/");
	};

	gotoSchool(schoolId: string): void {
		this.router.navigateByUrl("/CollegeScorecard/School/" + schoolId);
	};

	//shared functions for formating college data
	studentSize(v: string): string {
		v = v.trim();
		if (v.length == 0)
			return "Data not available";
		else
			return "<span style='font-size:24px'>" + AppUtilityService.toRoundNumber(v, 0) + "</span><div style='font-size:12px'>Not Rated</div>";
	};

	cost(v: string, navg: string): string {
		v = v.trim();
		navg = navg.trim();
		if (v.length == 0) {
			return "Data not available";
		}
		else {
			var m = "", c = "";
			var fv = "<span style='font-size:24px'>" + (!isNaN(parseFloat(v)) ? "$" : "") + AppUtilityService.toRoundNumber(v, 2) + "</span>";
			if (navg.length == 0)
				return fv + "Not Rated";
			else {
				if (v == navg) { m = "<i class='fa fa-check fa-1' aria-hidden='true'/> About Average"; c = "2b6091"; }
				else if (v >= navg) { m = "<i class='icnArrowUp fa-1' aria-hidden='true'/> Above Average"; c = "dc4116"; }
				else { m = "<i class='icnArrowDown fa-1' aria-hidden='true'/> Lower Than Average"; c = "77b713"; }

				return fv + "<div title='Average: $" + AppUtilityService.toRoundNumber(navg, 2) + "' style='color:#" + c + ";font-size:12px;'>" + m + "</div>";
			}
		}
	};

	graduationRate(v: string, navg: string): string {
		v = v.trim();
		navg = navg.trim();
		if (v.length == 0) {
			return "Data not available";
		}
		else {
			var m = "", c = "";
			var fv = "<span style='font-size:24px'>" + (!isNaN(parseFloat(v)) ? Math.round(parseFloat(v) * 100) + "%" : "") + "</span>";
			if (navg.length == 0)
				return fv + "Not Rated";
			else {
				if (v == navg) { m = "<i class='fa fa-check fa-1' aria-hidden='true'/> About Average"; c = "2b6091"; }
				else if (v >= navg) { m = "<i class='icnArrowUp fa-1' aria-hidden='true'/> Above Average"; c = "77b713"; }
				else { m = "<i class='icnArrowDown fa-1' aria-hidden='true'/> Lower Than Average"; c = "dc4116"; }

				return fv + "<div title='Average: " + AppUtilityService.toRoundNumber(navg, 2) + "' style='color:#" + c + ";font-size:12px;'>" + m + "</div>";
			}
		}
	};

	salary(v: string, navg: string):string {
		v = v.trim();
		navg = navg.trim();
		if (v.length == 0) {
			return "Data not available";
		}
		else {
			var m = "", c = "";
			var fv = "<span style='font-size:24px'>" + (!isNaN(parseFloat(v)) ? "$" : "") + AppUtilityService.toRoundNumber(v, 2) + "</span>";
			if (navg.length == 0)
				return fv + "Not Rated";
			else {
				if (v == navg) { m = "<i class='fa fa-check fa-1' aria-hidden='true'/> About Average"; c = "2b6091"; }
				else if (v >= navg) { m = "<i class='icnArrowUp fa-1' aria-hidden='true'/> Above Average"; c = "77b713"; }
				else { m = "<i class='icnArrowDown fa-1' aria-hidden='true'/> Lower Than Average"; c = "dc4116"; }

				return fv + "<div title='Average: $" + AppUtilityService.toRoundNumber(navg, 2) + "' style='color:#" + c + ";font-size:12px;'>" + m + "</div>";
			}
		}
	}
}
